import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CarFront, RefreshCw, ShieldCheck } from "lucide-react";
import { fmt, inr } from "./format";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const istDay = (d) =>
  d.toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }); // YYYY-MM-DD

const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return istDay(d);
};

const PRESETS = [
  ["today", "Today", () => ({ start: istDay(new Date()), end: istDay(new Date()) })],
  ["7d", "Last 7 days", () => ({ start: daysAgo(6), end: istDay(new Date()) })],
  ["30d", "Last 30 days", () => ({ start: daysAgo(29), end: istDay(new Date()) })],
  ["mtd", "This month", () => ({ start: istDay(new Date()).slice(0, 8) + "01", end: istDay(new Date()) })],
  ["custom", "Custom", null],
];

const LABEL = { cashfree: "Cashfree", carinfo: "CarInfo", surepass: "Surepass", invincible: "Invincible" };
const name = (p) => LABEL[p] || p;

function Tile({ label, value, sub, accent = "slate", testid }) {
  const tone =
    accent === "emerald" ? "text-emerald-700" : accent === "rose" ? "text-rose-600" : "text-slate-900";
  return (
    <div className="rounded-xl border border-slate-200/80 bg-white px-4 py-3" data-testid={testid}>
      <div className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">{label}</div>
      <div className={`text-2xl font-bold leading-tight ${tone}`}>{value}</div>
      {sub && <div className="text-[11px] text-slate-400 mt-0.5 leading-tight">{sub}</div>}
    </div>
  );
}

/**
 * CRM → Dashboard → Reports: what the RC chain actually delivered in a window —
 * cars resolved, cars lost, who rescued them and the rupees per resolved car.
 * The spend card answers what it cost; this answers what we got for it.
 */
export default function RcChainAnalysis() {
  const [preset, setPreset] = useState("7d");
  const [range, setRange] = useState({ start: "", end: "" });
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const params = useMemo(() => {
    if (preset === "custom") return range.start && range.end ? range : null;
    return PRESETS.find(([k]) => k === preset)[2]();
  }, [preset, range]);

  const load = useCallback(async () => {
    if (!params) return;
    setBusy(true);
    try {
      const res = await axios.get(`${API_URL}/dashboard/services/providers/rc-chain`, { params });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load the RC chain analysis");
    } finally {
      setBusy(false);
    }
  }, [params]);

  useEffect(() => {
    load();
  }, [load]);

  const t = data?.totals;
  const rescued = Object.entries(t?.rescued_by || {}).sort((a, b) => b[1] - a[1]);
  const peak = (data?.by_day || []).reduce((mx, d) => Math.max(mx, d.lookups || 0), 0) || 1;
  const perDay = t && data?.days ? (t.dead / data.days).toFixed(1) : null;

  return (
    <div className="bg-white rounded-xl border border-slate-200/80 p-5" data-testid="rc-chain-analysis">
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <CarFront className="w-4 h-4 text-emerald-600" />
        <h3 className="text-sm font-semibold text-slate-700">RC chain — cars resolved vs cars lost</h3>
        <div className="flex items-center gap-1 ml-2">
          {PRESETS.map(([key, label]) => (
            <button
              key={key}
              onClick={() => setPreset(key)}
              className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                preset === key
                  ? "bg-slate-900 text-white"
                  : "text-slate-500 hover:bg-slate-100 border border-slate-200"
              }`}
              data-testid={`rc-chain-preset-${key}`}
            >
              {label}
            </button>
          ))}
        </div>
        {preset === "custom" && (
          <div className="flex items-center gap-1.5">
            <input
              type="date"
              value={range.start}
              onChange={(e) => setRange((r) => ({ ...r, start: e.target.value }))}
              className="px-2 py-1 text-xs border border-slate-200 rounded-md"
              data-testid="rc-chain-start"
            />
            <span className="text-xs text-slate-400">to</span>
            <input
              type="date"
              value={range.end}
              onChange={(e) => setRange((r) => ({ ...r, end: e.target.value }))}
              className="px-2 py-1 text-xs border border-slate-200 rounded-md"
              data-testid="rc-chain-end"
            />
          </div>
        )}
        <button
          onClick={load}
          disabled={busy}
          className="ml-auto p-1.5 rounded-lg text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 disabled:opacity-50"
          title="Refresh"
          data-testid="rc-chain-refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} />
        </button>
      </div>

      {!data ? (
        <div className="py-10 text-center text-sm text-slate-400" data-testid="rc-chain-empty">
          {preset === "custom" && !params ? "Pick a start and end date." : "Loading…"}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Tile
              label="Cars resolved"
              value={fmt(t.answered)}
              sub={t.answer_rate_pct != null ? `${t.answer_rate_pct}% of ${fmt(t.lookups)} paid lookups` : "no lookups yet"}
              accent="emerald"
              testid="rc-chain-answered"
            />
            <Tile
              label="Cars lost"
              value={fmt(t.dead)}
              sub={perDay ? `${perDay} a day — nobody in the chain could answer` : "none"}
              accent={t.dead ? "rose" : "slate"}
              testid="rc-chain-dead"
            />
            <Tile
              label="Rescued by a backup"
              value={fmt(t.rescued)}
              sub={rescued.length ? rescued.map(([p, n]) => `${name(p)} ${n}`).join(" · ") : "primary answered everything"}
              testid="rc-chain-rescued"
            />
            <Tile
              label="Cost per resolved car"
              value={t.inr_per_answered != null ? `₹${inr(t.inr_per_answered)}` : "—"}
              sub={`₹${inr(t.inr)} total · ${fmt(t.cache_served)} more served free from cache`}
              testid="rc-chain-cost"
            />
          </div>

          <div className="mt-4 space-y-1" data-testid="rc-chain-days">
            {data.by_day.map((d) => (
              <div key={d.key} className="flex items-center gap-3 text-xs">
                <span className="w-16 flex-shrink-0 text-slate-500">{d.label}</span>
                <div className="flex-1 h-3.5 bg-slate-100 rounded overflow-hidden flex">
                  <div
                    className="bg-emerald-500 h-full"
                    style={{ width: `${((d.answered || 0) / peak) * 100}%` }}
                    title={`${d.answered} resolved`}
                  />
                  <div
                    className="bg-rose-400 h-full"
                    style={{ width: `${((d.dead || 0) / peak) * 100}%` }}
                    title={`${d.dead} lost`}
                  />
                </div>
                <span className="w-24 text-right text-slate-500">
                  {d.answered} ok · <span className={d.dead ? "text-rose-600 font-semibold" : ""}>{d.dead} lost</span>
                </span>
                <span className="w-14 text-right font-semibold text-slate-700">₹{inr(d.inr)}</span>
              </div>
            ))}
          </div>

          <div className="mt-5 overflow-x-auto">
            <h4 className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5 text-slate-400" />
              Vendor scorecard
              <span className="font-normal text-slate-400">
                chain today: {(data.chain || []).map(name).join(" → ")}
              </span>
            </h4>
            <table className="w-full text-xs" data-testid="rc-chain-scorecard">
              <thead>
                <tr className="text-slate-400 text-[10px] uppercase tracking-wide">
                  <th className="text-left font-medium py-1.5">Vendor</th>
                  <th className="text-right font-medium">Called</th>
                  <th className="text-right font-medium">Answered</th>
                  <th className="text-right font-medium">Answer rate</th>
                  <th className="text-right font-medium">Rescues</th>
                  <th className="text-right font-medium">Spend</th>
                  <th className="text-right font-medium">Per answer</th>
                  <th className="text-right font-medium">Speed</th>
                </tr>
              </thead>
              <tbody>
                {data.providers.map((p) => (
                  <tr key={p.provider} className="border-t border-slate-100" data-testid={`rc-chain-row-${p.provider}`}>
                    <td className="py-1.5 font-semibold text-slate-700">{name(p.provider)}</td>
                    <td className="text-right text-slate-600">{fmt(p.calls)}</td>
                    <td className="text-right text-slate-600">{fmt(p.answered)}</td>
                    <td className="text-right">
                      <span className={p.answer_rate_pct >= 70 ? "text-emerald-700 font-semibold" : "text-amber-700"}>
                        {p.answer_rate_pct != null ? `${p.answer_rate_pct}%` : "—"}
                      </span>
                    </td>
                    <td className="text-right text-slate-600">{p.rescues ? fmt(p.rescues) : "—"}</td>
                    <td className="text-right text-slate-700">₹{inr(p.inr)}</td>
                    <td className="text-right text-slate-500">
                      {p.inr_per_answer != null ? `₹${inr(p.inr_per_answer)}` : "—"}
                    </td>
                    <td className="text-right text-slate-500">
                      {p.avg_ms != null ? `${(p.avg_ms / 1000).toFixed(1)}s` : "—"}
                    </td>
                  </tr>
                ))}
                {data.providers.length === 0 && (
                  <tr>
                    <td colSpan={8} className="py-4 text-center text-slate-400">
                      No paid RC calls in this window — every lookup came from cache.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {data.by_source.length > 0 && (
            <div className="mt-5" data-testid="rc-chain-sources">
              <h4 className="text-xs font-semibold text-slate-600 mb-2">Where the lookups came from</h4>
              <div className="grid sm:grid-cols-2 gap-2">
                {data.by_source.map((s) => (
                  <div
                    key={s.source}
                    className="flex items-center gap-2 text-xs rounded-lg border border-slate-200/80 px-3 py-2"
                    data-testid={`rc-chain-source-${s.source.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <span className="font-semibold text-slate-700 flex-1">{s.source}</span>
                    <span className="text-slate-500">{fmt(s.lookups)} lookups</span>
                    <span className="text-emerald-700">{fmt(s.answered)} ok</span>
                    {s.dead > 0 && <span className="text-rose-600 font-semibold">{fmt(s.dead)} lost</span>}
                    <span className="w-14 text-right text-slate-600">₹{inr(s.inr)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {data.dead_examples.length > 0 && (
            <div className="mt-5" data-testid="rc-chain-dead-list">
              <h4 className="text-xs font-semibold text-slate-600 mb-2">
                Cars nobody could answer — worth a manual check
              </h4>
              <div className="space-y-1">
                {data.dead_examples.map((d) => (
                  <div key={`${d.plate}-${d.at}`} className="text-[11px] text-slate-500 flex gap-2 flex-wrap">
                    <span className="font-semibold text-slate-700">{d.plate || "—"}</span>
                    <span className="text-slate-400">{d.source}</span>
                    <span className="text-slate-400">tried {d.tried.map(name).join(" → ")}</span>
                    <span className="flex-1 truncate" title={d.error}>
                      {d.error}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <p className="mt-4 text-[11px] text-slate-400">
            One lookup writes one row per vendor tried, so a plate Cashfree missed and CarInfo
            answered counts as one resolved car and one rescue — not two calls. Money uses the same
            billing rules as the spend card: Cashfree and CarInfo bill answered calls only, hard
            failures and no-record errors are free, and the Surepass rate is an assumption.
          </p>
        </>
      )}
    </div>
  );
}
